import EventEmitter from "event-lite";
import {
  createStore,
  delMany,
  get,
  getMany,
  set,
  setMany,
  values,
} from "idb-keyval";

import {
  IS_APP,
  IS_DEV,
  IS_NODE,
  isPersistent,
} from "@/__main__/constants.mjs";
import blitzMessage, { EVENTS } from "@/__main__/ipc-core.mjs";
import mainRefs from "@/__main__/refs.mjs";
import createQueue from "@/util/create-queue.mjs";
import { devDebug, devError, devWarn } from "@/util/dev.mjs";
import globals from "@/util/global-whitelist.mjs";
import symbolName, { symbolMap } from "@/util/symbol-name.mjs";
// import { timeSlice } from "@/util/time-slice.mjs";
import initLZ4, { compress, decompress } from "@/vendor/lz4_wasm.mjs";

const SYNC_PREFIX = "blitz-sync";
const SYNC_KEYS = ["settings", "user"];
const SYNC_DEBOUNCE_TIME = 5_000;
let syncDebounceTimer = null;
const pendingKeysToSync = new Set();

const isOverlayRoute = /^\/(.*?)\/overlay(?:$|\/)/.test(
  globals?.location?.pathname,
);

export const __PRIVATE_DB_SYMBOL_FOR_INTERNAL_USE_NOT_APPLICATION_CODE =
  symbolName("private-db");

const store = !IS_NODE ? createStore("blitz", "blitz-app") : null;

const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8");
const wasmInit = IS_NODE
  ? Promise.resolve(false)
  : // Careful: this import string must be static, so that esbuild can
    // automatically resolve the path as an entry point.
    import("@/vendor/lz4_wasm_bg.wasm")
      .then(({ default: wasmbin }) => initLZ4(wasmbin))
      .then(() => {
        // Now usable: compress, decompress
        return true;
      })
      .catch((error) => {
        devError(`Failed to load WASM module: ${error}`);
        return false;
      });

const writeQueue = createQueue();
let debounceExpiryTimer;

export function wrapDBMethod(fn, returnValue) {
  if (IS_NODE && !fn.override) {
    return () => Promise.resolve(returnValue);
  }
  return function wrappedMethod() {
    return fn(...arguments).catch((error) => {
      devError("DB FAILED", error);
      return returnValue;
    });
  };
}

function hasCustomExpiryTime(value) {
  return (
    value &&
    typeof value === "object" &&
    typeof value[isPersistent] === "number"
  );
}

const VOLATILE_PATH = "volatile";
const DID_INIT = "DID_INIT";
const EXPIRY_KEY = "$$expiry";

// This is a lower level module that you shouldn't use directly,
// use writeState instead.
const db = {
  // A good heuristic for this is how long renders are expected to take. Writes
  // should occur after renders and not block rendering.
  // This is also the duration of time during which calls can be batched together.
  WRITE_DEBOUNCE_TIME: 250,

  // max 7 days until eviction by default
  // 3 hrs for devs :^)
  RECORD_LIFETIME: IS_DEV ? 1000 * 60 * 60 * 3 : 1000 * 60 * 60 * 24 * 7,
  EXPIRY_KEY,
  DID_INIT,
  excludeFromExpiry: {
    [EXPIRY_KEY]: true,
    settings: true,
  },

  _init: init,
  _get: wrapDBMethod(get),
  _getMany: wrapDBMethod(getMany, []),
  _set: wrapDBMethod(set),
  _setMany: wrapDBMethod(setMany),
  _delMany: wrapDBMethod(delMany),
  _values: wrapDBMethod(values),

  _events: new EventEmitter(),
  _isInitializing: false,
  _getManyMap: {},
  _setManyQueue: [],
  _expiryRecord: null,
  _symbolKeyAllowList: [isPersistent],
  _deserializeValue: deserializeValue,
  _serializeValue: serializeValue,

  // This is a semaphore that only allows one of each operation to occur at once.
  _getManyPromise: null,
  _setManyPromise: null,

  async find(ids) {
    const start = Date.now();

    await init();

    if (!Array.isArray(ids)) ids = [ids];

    for (const id of ids) {
      if (IS_DEV && id.startsWith(VOLATILE_PATH)) {
        devWarn(`Invalid read of "${VOLATILE_PATH}" path for id "${id}"`);
      }
      db._getManyMap[id] = null;
    }

    const result = await processGetMany(start);

    const foundValues = await Promise.all(
      ids.map((id) => {
        let value = result[id];

        if (value instanceof Uint8Array) {
          try {
            value = JSON.parse(decoder.decode(decompress(value)));
          } catch (error) {
            devError("Failed to parse value from db", {
              error,
              id,
              raw: value,
            });

            return null;
          }
        }

        return IS_NODE ? value : deserializeValue(value);
      }),
    );

    // extend expiry time of read records
    for (let i = 0; i < ids.length; ++i) {
      const id = ids[i];
      const value = foundValues[i];

      if (
        !db.excludeFromExpiry.hasOwnProperty(id) &&
        !hasCustomExpiryTime(value)
      ) {
        const currentValue = db._expiryRecord[id];
        const expiryTime = Date.now() + db.RECORD_LIFETIME;
        if (!currentValue || currentValue < expiryTime) {
          db._expiryRecord[id] = expiryTime;
        }
      }
    }

    writeExpiry();

    return foundValues;
  },

  async upsert(entries) {
    const start = Date.now();
    const hasCompression = await init();
    if (!Array.isArray(entries)) entries = [entries];
    if (!Array.isArray(entries[0])) entries[0] = [arguments[0], arguments[1]];

    if (IS_APP && !isOverlayRoute) {
      for (const [id] of entries) {
        if (SYNC_KEYS.includes(id)) {
          pendingKeysToSync.add(id);
          debounceSyncWithCore();
        }
      }
    }

    // O(n^2) time complexity I know, but real world cases it won't matter.
    // There are bigger problems if you have to write a ton of data to disk at once.
    for (const [id, value] of entries) {
      if (IS_DEV && id.startsWith(VOLATILE_PATH)) {
        devWarn(`Invalid write of "${VOLATILE_PATH}" path for id "${id}"`);
      }
      if (!db.excludeFromExpiry.hasOwnProperty(id)) {
        let expiryTime = Date.now() + db.RECORD_LIFETIME;
        if (hasCustomExpiryTime(value)) {
          expiryTime = value[isPersistent];
        }
        db._expiryRecord[id] = expiryTime;
      }

      const sanitizedValue = IS_NODE ? value : await serializeValue(value);
      const finalValue = hasCompression
        ? compress(encoder.encode(JSON.stringify(sanitizedValue)))
        : sanitizedValue;
      const index = db._setManyQueue.findIndex(([entryId]) => entryId === id);
      if (~index) {
        db._setManyQueue[index][1] = finalValue;
      } else {
        db._setManyQueue.push([id, finalValue]);
      }
    }
    // Update/extend expiry of written records.
    writeExpiry();
    return processSetMany(start);
  },

  async getSize() {
    const start = Date.now();
    const values = await db._values(store);
    let cSize = 0;
    let uSize = 0;
    for (const value of values) {
      if (value instanceof Uint8Array) cSize += value.byteLength;
      else uSize += JSON.stringify(value).length;
    }
    devDebug(`db get size (${Date.now() - start} ms)`);
    return (
      `${Math.ceil(cSize / 1024)} KB compressed, ` +
      `${Math.ceil(uSize / 1024)} KB uncompressed`
    );
  },

  async deleteMany(ids) {
    if (!Array.isArray(ids)) ids = [ids];
    await db._delMany(ids, store);
  },
};

async function processGetMany(start) {
  if (!db._getManyPromise) {
    db._getManyPromise = new Promise((resolve, reject) => {
      setTimeout(() => {
        const ids = Object.keys(db._getManyMap);
        db._getManyPromise = null;
        db._getManyMap = {};
        db._getMany(ids, store).then((result) => {
          resolve(
            result.reduce((h, v, i) => {
              const id = ids[i];
              h[id] = v;
              return h;
            }, {}),
          );
          devDebug(`db get (${Date.now() - start} ms)`, ids);
        }, reject);
      }, 0);
    });
  }
  const result = await db._getManyPromise;
  return result;
}

async function processSetMany(start) {
  if (!db._setManyPromise) {
    db._setManyPromise = new Promise((resolve, reject) => {
      setTimeout(() => {
        const entries = db._setManyQueue;
        db._setManyPromise = null;
        db._setManyQueue = [];
        db._setMany(entries, store).then((result) => {
          resolve(result);
          devDebug(
            `db set (${Date.now() - start - db.WRITE_DEBOUNCE_TIME} ms)`,
            entries.map(([id]) => id),
          );
        }, reject);
      }, db.WRITE_DEBOUNCE_TIME);
    });
  }
  const result = await db._setManyPromise;
  return result;
}

function writeExpiry() {
  clearTimeout(debounceExpiryTimer);
  debounceExpiryTimer = setTimeout(() => {
    writeQueue.push(() => {
      const start = Date.now();
      db._set(db.EXPIRY_KEY, db._expiryRecord, store);
      devDebug(`db expiry (${Date.now() - start} ms)`, db._expiryRecord);
    });
  }, db.WRITE_DEBOUNCE_TIME);
}

async function init() {
  const hasCompression = await wasmInit;
  if (db._expiryRecord) return hasCompression;
  if (db._isInitializing) {
    return new Promise((resolve) => {
      db._events.once(db.DID_INIT, () => {
        resolve(hasCompression);
      });
    });
  }
  db._isInitializing = true;

  let expiryRecord = await db._get(db.EXPIRY_KEY, store);
  if (!expiryRecord) {
    expiryRecord = {};
    await db._set(db.EXPIRY_KEY, expiryRecord, store);
  }
  await purgeRecords(expiryRecord);

  db._expiryRecord = expiryRecord;

  if (IS_APP) {
    try {
      await readDBFromCore();
    } catch (err) {
      devError("[DB] Failed to sync with blitz-core", err);
    }
  }

  // Complete initialization
  db._isInitializing = false;
  db._events.emit(db.DID_INIT);

  return hasCompression;
}

async function purgeRecords(expiryRecord) {
  const expiredIds = [];
  const now = Date.now();
  for (const id in expiryRecord) {
    const time = expiryRecord[id];
    if (now > time) {
      expiredIds.push(id);
      delete expiryRecord[id];
    }
  }
  const start = Date.now();
  await db.deleteMany(expiredIds);
  devDebug(`db purge (${Date.now() - start} ms)`, expiredIds);
}

export async function deserializeValue(value) {
  if (!(value && typeof value === "object")) {
    if (typeof value === "string" && symbolMap.hasOwnProperty(value)) {
      return symbolMap[value];
    }

    return value;
  }

  if (value.type === "BigInt") return BigInt(value.value);

  if (value.type === "Date") return new Date(value.value);

  if (value.type === "Uint8Array") return new Uint8Array(value.value);
  if (value.type === "Uint16Array") return new Uint16Array(value.value);

  const clone = Array.isArray(value) ? [] : {};
  for (const k in value) {
    const safeValue = await deserializeValue(value[k]);
    if (typeof safeValue === "object" && safeValue?.type === "SymbolKey")
      clone[safeValue.symbol] = safeValue.value;
    else clone[k] = safeValue;
  }

  return clone;
}

export async function serializeValue(value, seen, path = []) {
  // Temporarily disable access warnings while serializing a value
  mainRefs.enableAccessWarnings = false;

  const serializedValue = await deeplySerializeValue(value, seen, path);

  mainRefs.enableAccessWarnings = true;

  return serializedValue;
}

async function deeplySerializeValue(value, state, path = []) {
  if (!state)
    state = {
      seen: new WeakSet(),
      seenPaths: new WeakMap(),
    };

  const { seen, seenPaths } = state;

  if (!(value && typeof value === "object")) {
    if (
      typeof value === "symbol" &&
      symbolMap.hasOwnProperty(value.description)
    ) {
      return value.description;
    }

    if (typeof value === "bigint") {
      value = {
        type: "BigInt",
        value: value.toString(),
      };
    }

    return value;
  }

  // TODO: This does detect cyclic references, but it also will throw false
  // positives on objects with the same identity, regardless of if its a cyclical
  // reference. We need to figure out a better way to actually detect cycles, but
  // for now, a workaround is to simply clone the object if this error trips.
  if (seen.has(value)) {
    devError(
      `Cyclic graph closed!

Previous path: "${(seenPaths.get(value) ?? []).join(".")}"
Current path: "${path.join(".")}"
`,
      value,
    );
    throw new Error("Cyclic graph found!");
  }

  if (value instanceof Date) return { type: "Date", value };

  if (value instanceof Uint8Array)
    return { type: "Uint8Array", value: [...value] };
  if (value instanceof Uint16Array)
    return { type: "Uint16Array", value: [...value] };

  // pls be careful to not include Date and other value types in here
  seen.add(value);
  if (IS_DEV) seenPaths.set(value, [...path]);

  const isArray = Array.isArray(value);
  const clone = isArray ? [] : {};

  // This doesn't include symbols that may be included on the obj/array
  for (const k in value) {
    clone[k] = await deeplySerializeValue(
      value[k],
      state,
      // micro-optimization to avoid cloning on prod because this is only
      // used for dev debug purposes
      IS_DEV ? [...path, k] : path,
    );
  }

  // Allow select symbols through
  for (const key of db._symbolKeyAllowList) {
    if (value[key] !== undefined) {
      const safeValue = {
        type: "SymbolKey",
        symbol: key.description,
        value: await deeplySerializeValue(
          value[key],
          state,
          // micro-optimization to avoid cloning on prod because this is only
          // used for dev debug purposes
          IS_DEV ? [...path, key] : path,
        ),
      };

      if (isArray) clone.push(safeValue);
      else clone[key.description] = safeValue;
    }
  }

  return clone;
}

async function readDBFromCore() {
  if (IS_NODE) return;

  try {
    devDebug(`[DB] Pulling ${SYNC_KEYS.length} keys from blitz-core`);
    const start = Date.now();

    for (const key of SYNC_KEYS) {
      try {
        const response = await blitzMessage(
          EVENTS.DB_READ,
          `${SYNC_PREFIX}-${key}`,
        );
        if (!response) continue;

        const data = JSON.parse(response);
        const binary = atob(data);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }

        await db._set(key, bytes, store);

        // Update expiry time
        if (!db.excludeFromExpiry.hasOwnProperty(key)) {
          db._expiryRecord[key] = Date.now() + db.RECORD_LIFETIME;
        }
      } catch (err) {
        devError(`[DB] Failed to read key ${key} from blitz-core`, err);
      }
    }

    writeExpiry();
    devDebug(`[DB] blitz-core pull completed in ${Date.now() - start}ms`);
  } catch (err) {
    devError("[DB] Error reading from blitz-core", err);
  }
}

async function syncDBWithCore() {
  if (IS_NODE || pendingKeysToSync.size === 0) return;

  devDebug(
    `[DB] Syncing ${Array.from(pendingKeysToSync)
      .map((key) => `"${key}"`)
      .join(", ")} with blitz-core...`,
  );

  const start = Date.now();

  const keysArray = Array.from(pendingKeysToSync);
  const values = await db._getMany(keysArray, store);

  const entries = keysArray.map((key, i) => [key, values[i]]);
  const syncPromises = entries.map(async ([key, value]) => {
    if (value === undefined) return;

    try {
      let data;

      if (value instanceof Uint8Array) {
        // chunk into segments to avoid exceeding the max call stack size
        const CHUNK_SIZE = 8192;
        let binary = "";
        const len = value.length;

        for (let i = 0; i < len; i += CHUNK_SIZE) {
          const chunk = value.subarray(i, Math.min(i + CHUNK_SIZE, len));
          binary += String.fromCharCode.apply(null, chunk);
        }

        data = btoa(binary);
      } else {
        devDebug(`[DB] Skipping key ${key} with non-binary value`);
        return;
      }

      await blitzMessage(EVENTS.DB_WRITE, {
        key: `${SYNC_PREFIX}-${key}`,
        value: JSON.stringify(data),
      });
    } catch (err) {
      devError(`[DB] Failed to write key ${key} with blitz-core`, err);
    }
  });

  await Promise.all(syncPromises);
  pendingKeysToSync.clear();

  devDebug(`[DB] blitz-core sync completed in ${Date.now() - start}ms`);
}

function debounceSyncWithCore() {
  if (IS_NODE || pendingKeysToSync.size === 0) return;

  clearTimeout(syncDebounceTimer);
  syncDebounceTimer = setTimeout(() => {
    syncDBWithCore().catch((err) => {
      devError("[DB] Failed to sync with blitz-core", err);
    });
  }, SYNC_DEBOUNCE_TIME);
}

// TESTING
globals.__BLITZ_DEV__.db = db;
globals.__BLITZ_DEV__.syncDBWithCore = syncDBWithCore;

// Attempt to purge every day.
function purge() {
  setTimeout(
    async () => {
      await purgeRecords(db._expiryRecord);
      purge();
    },
    1000 * 60 * 60 * 24,
  );
}
if (!IS_NODE) {
  purge();
}

export default {
  [__PRIVATE_DB_SYMBOL_FOR_INTERNAL_USE_NOT_APPLICATION_CODE]: db,
};
